import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { ChevronDown, ChevronLeft, ChevronRight, Download, Loader2, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';

type AuditEntry = {
  id: string;
  actor_user_id: string;
  actor_email: string | null;
  actor_role: string;
  action: string;
  description: string | null;
  category: string;
  severity: string;
  result: string;
  reason: string | null;
  case_ref: string | null;
  target_type: string | null;
  target_id: string | null;
  organization_id: string | null;
  organization_name: string | null;
  metadata: Record<string, unknown> | null;
  before_data: Record<string, unknown> | null;
  after_data: Record<string, unknown> | null;
  created_at: string;
};

const PAGE_SIZE = 50;
const ALL = 'all';

/** Matches the support-session reason floor so both privileged flows agree. */
const MIN_EXPORT_REASON = 8;

const CATEGORIES = [
  'authentication', 'authorization', 'support_session', 'data_access',
  'tenant_lifecycle', 'financial', 'settings', 'approval', 'audit',
];
const SEVERITIES = ['low', 'sensitive_read', 'operational', 'destructive', 'critical'];
const RESULTS = ['success', 'failure', 'denied'];
const ROLES = ['super_admin', 'admin', 'owner', 'user', 'system'];

/** Turn a snake_case controlled-vocabulary token into a human label. */
const humanise = (value: string) => value.replace(/_/g, ' ');

const SEVERITY_VARIANT: Record<string, 'secondary' | 'destructive' | 'outline'> = {
  low: 'outline',
  sensitive_read: 'secondary',
  operational: 'secondary',
  destructive: 'destructive',
  critical: 'destructive',
};

const RESULT_VARIANT: Record<string, 'secondary' | 'destructive' | 'outline'> = {
  success: 'outline',
  failure: 'destructive',
  denied: 'destructive',
};

const toCsvValue = (value: unknown) => {
  const text = value == null ? '' : typeof value === 'object' ? JSON.stringify(value) : String(value);
  return `"${text.replace(/"/g, '""')}"`;
};

/**
 * Native single-select styled to match the rest of the console. A plain
 * <select> keeps the whole filter row keyboard- and screen-reader-native (role
 * combobox with the same accessible names) without a floating listbox layer.
 */
const FilterSelect = ({
  label,
  allLabel,
  value,
  options,
  onChange,
}: {
  label: string;
  allLabel: string;
  value: string;
  options: string[];
  onChange: (value: string) => void;
}) => (
  <div className="relative">
    <select
      aria-label={label}
      value={value}
      onChange={(event) => onChange(event.target.value)}
      className="h-10 w-full appearance-none rounded-md border border-input bg-background px-3 py-2 pr-9 text-sm capitalize ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
    >
      <option value={ALL}>{allLabel}</option>
      {options.map((option) => (
        <option key={option} value={option} className="capitalize">{humanise(option)}</option>
      ))}
    </select>
    <ChevronDown className="pointer-events-none absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 opacity-50" />
  </div>
);

const AuditLog = () => {
  const [searchParams] = useSearchParams();
  const { toast } = useToast();
  const [category, setCategory] = useState(ALL);
  const [result, setResult] = useState(ALL);
  const [severity, setSeverity] = useState(ALL);
  const [role, setRole] = useState(ALL);
  const [actor, setActor] = useState('');
  const [tenant, setTenant] = useState(searchParams.get('org') ?? '');
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [page, setPage] = useState(0);
  const [exporting, setExporting] = useState(false);
  const [exportOpen, setExportOpen] = useState(false);
  const [exportReason, setExportReason] = useState('');

  const exportReasonValid = exportReason.trim().length >= MIN_EXPORT_REASON;
  const exportReasonRef = useRef<HTMLTextAreaElement>(null);

  const closeExport = useCallback(() => {
    setExportOpen(false);
    setExportReason('');
  }, []);

  // Backdrop and Escape are dismissals, never confirmations: they only ever
  // close, and they stand down while an export is in flight (matching the
  // disabled Cancel button) so an accidental click cannot race the request.
  const requestCloseExport = useCallback(() => {
    if (exporting) return;
    closeExport();
  }, [closeExport, exporting]);

  useEffect(() => {
    if (!exportOpen) return;
    exportReasonRef.current?.focus();
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') requestCloseExport();
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [exportOpen, requestCloseExport]);

  const filters = useMemo(
    () => ({
      _org_id: tenant || null,
      _actor_user_id: actor || null,
      _role: role === ALL ? null : role,
      _category: category === ALL ? null : category,
      _result: result === ALL ? null : result,
      _severity: severity === ALL ? null : severity,
      _from: from ? new Date(from).toISOString() : null,
      _to: to ? new Date(to).toISOString() : null,
    }),
    [tenant, actor, role, category, result, severity, from, to],
  );

  const { data = [], isLoading } = useQuery({
    queryKey: ['platform-audit', filters, page],
    queryFn: async () => {
      const { data, error } = await supabase.rpc('list_audit_log' as never, {
        ...filters,
        _limit: PAGE_SIZE,
        _offset: page * PAGE_SIZE,
      } as never);
      if (error) throw error;
      return (data || []) as AuditEntry[];
    },
  });

  const resetPage = () => setPage(0);

  // The export is itself an audited, privileged disclosure, so the reason must
  // be one the administrator actually typed. We never synthesise it on their
  // behalf — the server records exactly what was entered here.
  const handleExport = async () => {
    if (!exportReasonValid) return;
    const reason = exportReason.trim();
    setExporting(true);
    try {
      const { data, error } = await supabase.rpc('export_audit_log' as never, {
        ...filters,
        _reason: reason,
      } as never);
      if (error) throw error;
      const rows = (data || []) as AuditEntry[];
      const headers = ['created_at', 'actor_email', 'actor_role', 'action', 'description', 'category', 'severity', 'result', 'reason', 'organization_name', 'metadata'];
      const lines = [
        headers.join(','),
        ...rows.map((row) => headers.map((key) => toCsvValue((row as Record<string, unknown>)[key])).join(',')),
      ];
      const blob = new Blob([lines.join('\n')], { type: 'text/csv' });
      const url = URL.createObjectURL(blob);
      const anchor = document.createElement('a');
      anchor.href = url;
      anchor.download = `audit-log-${new Date().toISOString().slice(0, 10)}.csv`;
      anchor.click();
      URL.revokeObjectURL(url);
      toast({ title: 'Audit export ready', description: `${rows.length} events exported.` });
      closeExport();
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Unexpected error';
      toast({ title: 'Unable to export audit log', description: message, variant: 'destructive' });
    } finally {
      setExporting(false);
    }
  };

  return (
    <div className="p-4 md:p-8">
      <div className="mb-6 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h2 className="font-display text-3xl text-foreground">Audit Log</h2>
          <p className="mt-1 text-muted-foreground">Plain-English record of privileged platform activity. Append-only and non-forgeable.</p>
        </div>
        <Button variant="outline" className="gap-2" onClick={() => setExportOpen(true)}>
          <Download className="h-4 w-4" /> Export CSV
        </Button>
      </div>

      {exportOpen && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 p-4"
          // mousedown (not click) with an identity check: a drag that starts on
          // the panel and ends on the backdrop must not dismiss, and a backdrop
          // press can never reach the Export button underneath it.
          onMouseDown={(event) => {
            if (event.target === event.currentTarget) requestCloseExport();
          }}
        >
          <div
            role="dialog"
            aria-modal="true"
            aria-labelledby="audit-export-title"
            aria-describedby="audit-export-description"
            className="relative grid w-full max-w-lg gap-4 rounded-lg border bg-background p-6 shadow-lg"
          >
            <div className="flex flex-col space-y-1.5 text-center sm:text-left">
              <h3 id="audit-export-title" className="text-lg font-semibold leading-none tracking-tight">
                Export audit log
              </h3>
              <p id="audit-export-description" className="text-sm text-muted-foreground">
                Exporting is itself a recorded disclosure. Your reason is stored against your
                account alongside the filters currently applied.
              </p>
            </div>

            <div className="space-y-2">
              <Label htmlFor="audit-export-reason">Reason (required)</Label>
              <Textarea
                id="audit-export-reason"
                ref={exportReasonRef}
                aria-label="Audit export reason"
                value={exportReason}
                onChange={(event) => setExportReason(event.target.value)}
                placeholder="Why do you need this export? (min 8 characters)"
              />
            </div>

            <div className="flex flex-col-reverse sm:flex-row sm:justify-end sm:space-x-2">
              <Button variant="ghost" onClick={closeExport} disabled={exporting}>
                Cancel
              </Button>
              <Button onClick={handleExport} disabled={!exportReasonValid || exporting} className="gap-2">
                {exporting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />} Export
              </Button>
            </div>

            <button
              type="button"
              onClick={requestCloseExport}
              disabled={exporting}
              className="absolute right-4 top-4 rounded-sm opacity-70 ring-offset-background transition-opacity hover:opacity-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none"
            >
              <X className="h-4 w-4" />
              <span className="sr-only">Close</span>
            </button>
          </div>
        </div>
      )}

      <Card>
        <CardContent className="p-4 md:p-6">
          <div className="mb-4 grid gap-3 md:grid-cols-4">
            <FilterSelect
              label="Filter by category"
              allLabel="All categories"
              value={category}
              options={CATEGORIES}
              onChange={(value) => { setCategory(value); resetPage(); }}
            />

            <FilterSelect
              label="Filter by result"
              allLabel="All results"
              value={result}
              options={RESULTS}
              onChange={(value) => { setResult(value); resetPage(); }}
            />

            <FilterSelect
              label="Filter by severity"
              allLabel="All severities"
              value={severity}
              options={SEVERITIES}
              onChange={(value) => { setSeverity(value); resetPage(); }}
            />

            <FilterSelect
              label="Filter by role"
              allLabel="All roles"
              value={role}
              options={ROLES}
              onChange={(value) => { setRole(value); resetPage(); }}
            />

            <Input aria-label="Tenant organisation ID" value={tenant} onChange={(e) => { setTenant(e.target.value); resetPage(); }} placeholder="Tenant organisation ID" />
            <Input aria-label="Actor user ID" value={actor} onChange={(e) => { setActor(e.target.value); resetPage(); }} placeholder="Actor user ID" />
            <Input aria-label="From date" type="date" value={from} onChange={(e) => { setFrom(e.target.value); resetPage(); }} />
            <Input aria-label="To date" type="date" value={to} onChange={(e) => { setTo(e.target.value); resetPage(); }} />
          </div>

          {isLoading ? (
            <div className="flex h-64 items-center justify-center"><Loader2 className="h-8 w-8 animate-spin text-primary" /></div>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>When</TableHead>
                    <TableHead>Event</TableHead>
                    <TableHead>Who</TableHead>
                    <TableHead>Tenant</TableHead>
                    <TableHead>Result</TableHead>
                    <TableHead>Severity</TableHead>
                    <TableHead>Details</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {data.map((entry) => (
                    <TableRow key={entry.id}>
                      <TableCell className="whitespace-nowrap text-sm">{new Date(entry.created_at).toLocaleString()}</TableCell>
                      <TableCell>
                        <div className="font-medium">{entry.description || humanise(entry.action)}</div>
                        {entry.reason && <div className="text-xs text-muted-foreground">Reason: {entry.reason}</div>}
                        <div className="text-xs capitalize text-muted-foreground">{humanise(entry.category)}</div>
                      </TableCell>
                      <TableCell>
                        <div className="text-sm">{entry.actor_email || entry.actor_user_id}</div>
                        <div className="text-xs text-muted-foreground">{entry.actor_role}</div>
                      </TableCell>
                      <TableCell>{entry.organization_name || entry.organization_id || '—'}</TableCell>
                      <TableCell><Badge variant={RESULT_VARIANT[entry.result] ?? 'outline'} className="capitalize">{humanise(entry.result)}</Badge></TableCell>
                      <TableCell><Badge variant={SEVERITY_VARIANT[entry.severity] ?? 'outline'} className="capitalize">{humanise(entry.severity)}</Badge></TableCell>
                      <TableCell>
                        <details>
                          <summary className="cursor-pointer text-primary">Technical details</summary>
                          <pre className="mt-2 max-w-sm whitespace-pre-wrap rounded bg-muted p-2 text-xs">{JSON.stringify(
                            {
                              action: entry.action,
                              target_type: entry.target_type,
                              target_id: entry.target_id,
                              case_ref: entry.case_ref,
                              metadata: entry.metadata,
                              before: entry.before_data,
                              after: entry.after_data,
                            },
                            null,
                            2,
                          )}</pre>
                        </details>
                      </TableCell>
                    </TableRow>
                  ))}
                  {data.length === 0 && <TableRow><TableCell colSpan={7} className="h-24 text-center text-muted-foreground">No audit entries found</TableCell></TableRow>}
                </TableBody>
              </Table>
            </div>
          )}

          <div className="mt-4 flex items-center justify-end gap-2">
            <Button variant="outline" size="sm" disabled={page === 0} onClick={() => setPage((value) => Math.max(0, value - 1))}><ChevronLeft className="h-4 w-4" /> Previous</Button>
            <Button variant="outline" size="sm" disabled={data.length < PAGE_SIZE} onClick={() => setPage((value) => value + 1)}>Next <ChevronRight className="h-4 w-4" /></Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default AuditLog;
